import React, { useMemo } from 'react';
import { Plan, PricingVariant } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { useStyles } from '@wix/tpa-settings/react';
import { useCurrencyFormatter, useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import { useDynamicElement, useIsHydrated, useStylesParams } from '../../../../../hooks';
import { useLocale } from '../../../../../hooks/useLocale';
import { getVariantAdditionalFees } from '../../../../../utils/plan-utils';
import { st, classes } from './SetupFee.st.css';

interface Props {
  plan: Plan;
  variant: PricingVariant | undefined;
  planId: string;
  highlighted: boolean;
  className?: string;
}

export const SetupFee: React.FC<Props> = ({ plan, variant, planId, highlighted, className }) => {
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const { heightByPlanId, registerRef } = useDynamicElement('setup-fee');
  const { locale } = useLocale();
  const isHydrated = useIsHydrated();
  const styles = useStyles();
  const stylesParams = useStylesParams();
  const fontSize = styles.get(stylesParams.planSetupFeeFontSize);
  const isFontSizeEnabled = experiments.enabled(TPA_EXPERIMENTS.FONT_SIZE_FOR_TPA_ELEMENTS);

  const fees = getVariantAdditionalFees({
    variant: variant!,
    currency: plan.currency,
    locale,
    createCurrencyFormatter: useCurrencyFormatter,
    hideTrailingFractionZeroes: true,
  })
    .map(({ amount, name }) => `+${amount} ${name}`)
    .join('\n');

  const ref = useMemo(() => registerRef(planId), [planId, registerRef]);
  const dynamicHeight = useMemo(() => ({ height: heightByPlanId.get(planId) }), [heightByPlanId, planId]);

  return (
    <div
      className={st(classes.root, { highlighted, mobile: isMobile }, className)}
      data-hook={VIEWER_HOOKS.PLAN_SETUP_FEE}
      // setting inline styles causes react to re-render component on hydration, so ony set after hydration
      style={!isMobile && isHydrated ? dynamicHeight : {}}
    >
      <div
        className={classes.content}
        ref={!isMobile ? ref : undefined}
        style={isFontSizeEnabled ? { fontSize: `${fontSize}px` } : undefined}
      >
        {fees}
      </div>
    </div>
  );
};
